/**
 * Sell category search redirects for retired / renamed subcategories.
 *
 * Owner for empty-results “Did you mean…?” — do not scatter redirect strings in UI.
 * Catalog names come from deprecated replacement maps; vernacular extras live here.
 */

import { categoryLabels } from "@/lib/categoryLabels";
import {
  DEPRECATED_APPLIANCE_INVERTER_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_AUDIO_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_FAN_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_FRIDGE_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_FURNITURE_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_GAS_COOKER_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_KITCHEN_CABINET_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_SEWING_MACHINE_SUBCATEGORY_REPLACEMENTS,
  DEPRECATED_SNOOKER_BOARD_SUBCATEGORY_REPLACEMENTS,
  subcategories,
  type CategoryStructure,
  type SubCategory,
} from "@/lib/categoryStructure";
import type { CategorySelection, ItemCategory } from "@/lib/types";

export type SellSubcategorySearchRedirect = {
  /** Normalized query fragment that matched. */
  matchedTerm: string;
  targetSubcategoryId: string;
  subcategoryName: string;
  categoryId: ItemCategory;
  categoryName: string;
  /** Plain-language why this appears (retired name → live pick). */
  explanation: string;
};

type VernacularAlias = {
  /** Human phrases (any casing / hyphenation). */
  terms: readonly string[];
  targetSubcategoryId: string;
  /** Optional override; default explains retired → live name. */
  explanation?: string;
};

/**
 * Extra phrases people type that are not exact legacy catalog names.
 * Keep short; prefer deriving from DEPRECATED_* maps when the old id still has a name.
 */
const VERNACULAR_SELL_SEARCH_ALIASES: readonly VernacularAlias[] = [
  {
    terms: [
      "l shaped sofa",
      "lshaped sofa",
      "l shape sofa",
      "sectional",
      "sectional sofa",
    ],
    targetSubcategoryId: "sofa-couche",
    explanation:
      "L-shaped sofas are listed under Sofa — pick standard or L-shaped in details",
  },
  {
    terms: ["l shaped chair", "lshaped chair", "l shape chair"],
    targetSubcategoryId: "sofa-couche",
    explanation:
      "L-shaped seating is listed under Sofa — pick standard or L-shaped in details",
  },
];

/** All taxonomy “old id → live ids” maps (single merge for search redirects). */
function allDeprecatedSubcategoryReplacements(): Readonly<
  Record<string, readonly string[]>
> {
  return {
    ...DEPRECATED_FURNITURE_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_APPLIANCE_INVERTER_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_FAN_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_GAS_COOKER_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_FRIDGE_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_KITCHEN_CABINET_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_SNOOKER_BOARD_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_SEWING_MACHINE_SUBCATEGORY_REPLACEMENTS,
    ...DEPRECATED_AUDIO_SUBCATEGORY_REPLACEMENTS,
  };
}

function normalizeSearchKey(value: string): string {
  return value.toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

function findSubcategoryInStructure(
  structure: CategoryStructure[],
  subcategoryId: string,
): SubCategory | null {
  for (const category of structure) {
    const sub = category.subcategories.find((s) => s.id === subcategoryId);
    if (sub) return sub;
  }
  return null;
}

function catalogNameForSubcategoryId(subcategoryId: string): string | null {
  return subcategories.find((s) => s.id === subcategoryId)?.name ?? null;
}

function termsMatchQuery(normalizedQuery: string, term: string): boolean {
  const normalizedTerm = normalizeSearchKey(term);
  if (!normalizedQuery || !normalizedTerm) return false;
  if (normalizedQuery === normalizedTerm) return true;
  // Require a meaningful fragment so short queries do not flood.
  if (normalizedQuery.length < 3) return false;
  return (
    normalizedTerm.includes(normalizedQuery) ||
    normalizedQuery.includes(normalizedTerm)
  );
}

function toSelection(
  structure: CategoryStructure[],
  targetSubcategoryId: string,
): CategorySelection | null {
  const sub = findSubcategoryInStructure(structure, targetSubcategoryId);
  if (!sub) return null;
  const categoryId = sub.parentCategory;
  return {
    categoryId,
    subcategoryId: targetSubcategoryId,
    categoryName: categoryLabels[categoryId] || categoryId,
    subcategoryName: sub.name,
  };
}

type AliasCandidate = {
  term: string;
  targetSubcategoryId: string;
  explanation: string;
};

function buildAliasCandidates(): AliasCandidate[] {
  const candidates: AliasCandidate[] = [];
  const replacements = allDeprecatedSubcategoryReplacements();

  for (const [legacyId, targets] of Object.entries(replacements)) {
    const legacyName = catalogNameForSubcategoryId(legacyId);
    if (!legacyName) continue;
    for (const targetId of targets) {
      const targetName = catalogNameForSubcategoryId(targetId) ?? targetId;
      candidates.push({
        term: legacyName,
        targetSubcategoryId: targetId,
        explanation: `"${legacyName}" is now listed as ${targetName}`,
      });
    }
  }

  for (const alias of VERNACULAR_SELL_SEARCH_ALIASES) {
    const targetName =
      catalogNameForSubcategoryId(alias.targetSubcategoryId) ??
      alias.targetSubcategoryId;
    for (const term of alias.terms) {
      candidates.push({
        term,
        targetSubcategoryId: alias.targetSubcategoryId,
        explanation:
          alias.explanation ?? `"${term}" is now listed as ${targetName}`,
      });
    }
  }

  return candidates;
}

/** Lazy-built once per module load. */
const ALIAS_CANDIDATES = buildAliasCandidates();

/**
 * When sell category search has no live hits, resolve redirects to current picks.
 * Only returns targets present in the given (usually active) structure.
 */
export function resolveSellSubcategorySearchRedirects(
  searchTerm: string,
  activeStructure: CategoryStructure[],
): SellSubcategorySearchRedirect[] {
  const normalizedQuery = normalizeSearchKey(searchTerm);
  if (normalizedQuery.length < 3) return [];

  const seen = new Set<string>();
  const results: SellSubcategorySearchRedirect[] = [];

  for (const candidate of ALIAS_CANDIDATES) {
    if (!termsMatchQuery(normalizedQuery, candidate.term)) continue;

    const selection = toSelection(
      activeStructure,
      candidate.targetSubcategoryId,
    );
    if (!selection) continue;

    const dedupeKey = `${normalizeSearchKey(candidate.term)}→${candidate.targetSubcategoryId}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    results.push({
      matchedTerm: candidate.term,
      targetSubcategoryId: selection.subcategoryId,
      subcategoryName: selection.subcategoryName,
      categoryId: selection.categoryId,
      categoryName: selection.categoryName,
      explanation: candidate.explanation,
    });
  }

  // Prefer unique targets; if several terms map to the same live id, keep first.
  const byTarget = new Map<string, SellSubcategorySearchRedirect>();
  for (const row of results) {
    if (!byTarget.has(row.targetSubcategoryId)) {
      byTarget.set(row.targetSubcategoryId, row);
    }
  }
  return [...byTarget.values()].sort((a, b) =>
    a.subcategoryName.localeCompare(b.subcategoryName),
  );
}

/**
 * Builds a CategorySelection for a redirect target from the active structure.
 */
export function selectionFromSellSearchRedirect(
  redirect: SellSubcategorySearchRedirect,
): CategorySelection {
  return {
    categoryId: redirect.categoryId,
    subcategoryId: redirect.targetSubcategoryId,
    categoryName: redirect.categoryName,
    subcategoryName: redirect.subcategoryName,
  };
}
